import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import {
  Phone, Search, Home, Bookmark, AlertCircle, Trash2, Square, CheckSquare,
  Mail, Clock,
} from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";
import { formatEuro } from "@/lib/format";
import QuickCallDialog from "@/components/QuickCallDialog";
import LiveStatusBadge from "@/components/LiveStatusBadge";
import StatusChip from "@/components/StatusChip";
import { formatTimeSince } from "@/lib/format";
import ConfirmDialog from "@/components/ConfirmDialog";
import LeadBulkBar, { leadDeleteMessage } from "@/components/LeadBulkBar";
import BulkArchiveDialog from "@/components/BulkArchiveDialog";
import LeadFilterChips, { readFilterFromParams, writeFilterToParams, filterToApiParams } from "@/components/LeadFilterChips";
import { useLeadSelection } from "@/lib/useLeadSelection";

/**
 * Brief verstuurd — makelaar leads waarvan minstens Brief 1 verstuurd is.
 * Sub-tabs Brief 1 / Brief 2 / Brief 3 plaatsen elke lead onder de hoogst
 * uitgevoerde brief. Per rij wordt aangetoond hoeveel tijd er nog rest tot
 * de volgende brief verstuurd moet worden (op basis van de gekoppelde
 * follow-up taak `Verstuur Brief N`).
 */

const BRIEF_TABS = [
  { k: "succes_brief", label: "Succes Brief", next: "Opvolging" },
  { k: "brief_1", label: "Brief 1", next: "Brief 2" },
  { k: "brief_2", label: "Brief 2", next: "Brief 3" },
  { k: "brief_3", label: "Brief 3", next: "Eindcontact" },
];

const SOURCE_TABS = [
  { k: "all",         label: "Alle" },
  { k: "makelaar",    label: "Makelaars" },
  { k: "particulier", label: "Particulieren" },
];

export default function BriefVerstuurd() {
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "succes_brief";
  const sourceTab = params.get("src") || "all"; // all | makelaar | particulier
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionConfig, setActionConfig] = useState({});  // {brief_1:{followup_days:30}, ...}
  const [callLead, setCallLead] = useState(null);
  const [query, setQuery] = useState("");
  const chipFilter = readFilterFromParams(params);
  const setChipFilter = (next) => setParams(writeFilterToParams(next, params));

  const reqIdRef = useRef(0);

  // Counts per (brief-tab, source-tab)
  const [counts, setCounts] = useState({});

  const refresh = async () => {
    const myId = ++reqIdRef.current;
    setLoading(true);
    const p = {
      brief_step: tab,
      ...filterToApiParams(chipFilter),
    };
    if (sourceTab !== "all") p.source_type = sourceTab;
    try {
      const r = await http.get("/api/leads", { params: p });
      if (myId !== reqIdRef.current) return;
      setLeads(r.data);
    } finally {
      if (myId === reqIdRef.current) setLoading(false);
    }
  };
  useEffect(() => { setLeads([]); refresh(); /* eslint-disable-next-line */ },
    [tab, sourceTab, params.get("f_status"), params.get("f_live"), params.get("f_option"), params.get("f_pmin"), params.get("f_pmax")]);

  // Fetch action config once for followup_days per brief
  useEffect(() => {
    http.get("/api/lead-actions").then(r => {
      const cfg = {};
      (r.data || []).forEach(a => { cfg[a.key] = a; });
      setActionConfig(cfg);
    }).catch(() => {});
  }, []);

  // Fetch counts per brief-tab respecting the current source-tab.
  useEffect(() => {
    let cancelled = false;
    const scopeParams = sourceTab !== "all" ? { source_type: sourceTab } : {};
    Promise.all(BRIEF_TABS.map(b =>
      http.get("/api/leads", { params: { ...scopeParams, brief_step: b.k } })
        .then(r => [b.k, r.data.length])
        .catch(() => [b.k, 0])
    )).then(entries => {
      if (cancelled) return;
      setCounts(Object.fromEntries(entries));
    });
    return () => { cancelled = true; };
  }, [sourceTab, params.get("f_status"), params.get("f_live"), params.get("f_option"), params.get("f_pmin"), params.get("f_pmax"), leads.length]);

  const filtered = useMemo(() => {
    if (!query.trim()) return leads;
    const q = query.toLowerCase();
    return leads.filter(l =>
      l.address?.toLowerCase().includes(q) ||
      l.postal_code?.includes(q) ||
      l.city?.toLowerCase().includes(q) ||
      l.agent_name?.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const openLead = (id) => {
    navigate(`/lead/${id}`, {
      state: { from: location.pathname + location.search, list: filtered.map(l => l.id) },
    });
  };

  const sel = useLeadSelection(refresh);

  const currentTab = BRIEF_TABS.find(b => b.k === tab) || BRIEF_TABS[0];

  return (
    <div className="flex-1 min-w-0 p-8 overflow-auto" data-testid="brief-verstuurd-screen">
      <div className="flex items-start justify-between mb-6">
        <div>
          <div className="text-[13px] text-[var(--teal-700)] font-medium mb-2 uppercase tracking-[0.14em]">
            Pipeline
          </div>
          <h1 className="font-display text-[32px] leading-tight text-[var(--ink-900)]">
            Brief verstuurd
          </h1>
          <p className="text-[14px] text-[var(--ink-500)] mt-1 max-w-2xl">
            Panden met minstens één brief-actie uitgevoerd. Elke pand staat in de
            tab van de hoogst-uitgevoerde brief (Succes Brief loopt parallel) en
            schuift automatisch op zodra je een volgende brief markeert.
          </p>
        </div>
      </div>

      {/* Source-type filter tabs */}
      <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="source-tabs">
        <span className="text-[11px] uppercase tracking-wider text-[var(--ink-500)] font-semibold mr-1">
          Bron:
        </span>
        {SOURCE_TABS.map(s => {
          const active = sourceTab === s.k;
          return (
            <button key={s.k} type="button"
                    data-testid={`source-tab-${s.k}`}
                    onClick={() => {
                      const n = new URLSearchParams(params);
                      if (s.k === "all") n.delete("src"); else n.set("src", s.k);
                      navigate(`${location.pathname}?${n.toString()}`);
                    }}
                    className={`px-2.5 h-7 rounded-full text-[11px] font-medium border transition
                      ${active
                        ? "bg-[var(--teal-700)] text-white border-[var(--teal-700)]"
                        : "bg-white text-[var(--ink-700)] border-[var(--border-soft)] hover:border-[var(--border-strong)]"}`}>
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Brief tabs */}
      <div className="flex items-center gap-3 mb-5 flex-wrap" data-testid="brief-tabs">
        <div className="flex items-center gap-1 bg-[var(--sand-100)] border border-[var(--border-soft)] rounded-lg p-1">
          {BRIEF_TABS.map(b => {
            const active = tab === b.k;
            return (
              <button key={b.k} type="button"
                      data-testid={`brief-tab-${b.k}`}
                      onClick={() => {
                        const n = new URLSearchParams(params);
                        n.set("tab", b.k);
                        navigate(`${location.pathname}?${n.toString()}`);
                      }}
                      className={`px-3 h-8 rounded-md text-[12px] font-medium transition flex items-center gap-1.5
                        ${active ? "bg-white text-[var(--teal-700)] shadow-sm"
                                 : "text-[var(--ink-500)] hover:text-[var(--ink-900)]"}`}>
                <Mail size={12} /> {b.label}
                <span className={`text-[11px] tabular ${active ? "text-[var(--ink-500)]" : "text-[var(--ink-400)]"}`}>
                  ({counts[b.k] ?? "…"})
                </span>
              </button>
            );
          })}
        </div>
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ink-400)]" />
          <input
            data-testid="brief-verstuurd-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={`${t("common.search")}…`}
            className="w-full h-10 pl-9 pr-3 rounded-lg border border-[var(--border-soft)] bg-white text-sm focus:border-[var(--teal-500)] outline-none"
          />
        </div>
      </div>

      <div className="mb-5">
        <LeadFilterChips value={chipFilter} onChange={setChipFilter}
                         sourceType={sourceTab === "all" ? undefined : sourceTab} />
      </div>

      {loading ? (
        <div className="text-[var(--ink-500)] text-sm">{t("common.loading")}</div>
      ) : filtered.length === 0 ? (
        <div className="card text-center py-12 px-6" data-testid="brief-verstuurd-empty">
          <h3 className="font-display text-[20px]">Geen panden in {currentTab.label}</h3>
          <p className="text-[14px] text-[var(--ink-500)] mt-2 max-w-md mx-auto">
            Wanneer je {currentTab.label} verstuurt vanuit een lead-detail, verschijnt
            het pand hier automatisch.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="brief-verstuurd-list">
          {filtered.map(lead => (
            <Row key={lead.id}
                 lead={lead}
                 briefKey={currentTab.k}
                 nextBriefLabel={currentTab.next}
                 followupDays={actionConfig[currentTab.k]?.followup_days || 30}
                 selected={sel.selected.has(lead.id)}
                 onToggle={() => sel.toggleOne(lead.id)}
                 onSelect={() => openLead(lead.id)}
                 onCall={() => setCallLead(lead)}
                 onDelete={() => sel.requestDelete([lead.id])} />
          ))}
        </ul>
      )}

      <LeadBulkBar
        count={sel.selected.size}
        total={filtered.length}
        allSelected={filtered.length > 0 && filtered.every(l => sel.selected.has(l.id))}
        onToggleAll={() => sel.toggleAll(filtered.map(l => l.id))}
        onClear={sel.clear}
        onArchive={() => sel.requestArchive(Array.from(sel.selected))}
        onDelete={() => sel.requestDelete(Array.from(sel.selected))}
      />

      <BulkArchiveDialog sel={sel} />

      <ConfirmDialog
        open={sel.confirmIds !== null}
        danger
        title={(sel.confirmIds?.length || 0) > 1 ? "Leads verwijderen" : "Lead verwijderen"}
        message={leadDeleteMessage(sel.confirmIds)}
        confirmLabel="Verwijderen"
        onConfirm={sel.doDelete}
        onCancel={sel.cancelDelete}
      />

      {callLead && (
        <QuickCallDialog lead={callLead} onClose={() => setCallLead(null)}
                         onSaved={() => { setCallLead(null); refresh(); }} />
      )}
    </div>
  );
}

function Row({ lead, briefKey, nextBriefLabel, followupDays, onSelect, onCall, onDelete, selected, onToggle }) {
  const briefInfo = lead.actions_done?.[briefKey];
  const executedAt = briefInfo?.executed_at;
  const countdown = computeCountdown(executedAt, followupDays);
  const hasPriceDrop = lead.old_price > 0 && lead.price > 0 && lead.old_price > lead.price;
  const thumb = lead.photos?.[0];
  const isOverdue = countdown && countdown.daysLeft < 0;
  return (
    <li onClick={onSelect}
        data-testid={`lead-row-${lead.id}`}
        className={`card cursor-pointer !p-4 grid grid-cols-12 gap-4 items-center transition-all
          ${selected
            ? "!border-[var(--teal-500)] !bg-[var(--sand-50)]"
            : isOverdue
              ? "!bg-[#FBE7E7] !border-[#EFBABA] hover:!bg-[#F8D5D5]"
              : "hover:border-[var(--border-strong)]"}`}>
      <div className="col-span-1 shrink-0 flex items-center gap-2">
        <button type="button" onClick={(e) => { e.stopPropagation(); onToggle?.(); }}
                data-testid={`lead-select-${lead.id}`}
                className="grid place-items-center w-6 h-6 text-[var(--ink-400)] hover:text-[var(--teal-700)]"
                title={selected ? "Deselecteren" : "Selecteren"}>
          {selected ? <CheckSquare size={18} className="text-[var(--teal-700)]" /> : <Square size={18} />}
        </button>
        <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--sand-200)] grid place-items-center">
          {thumb ? <img src={thumb} alt="" className="w-full h-full object-cover" loading="lazy" />
                 : <Home size={16} className="text-[var(--ink-400)]" />}
        </div>
      </div>
      <div className="col-span-4 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <StatusChip lead={lead} />
          {hasPriceDrop && (
            <span className="chip !py-[2px] !px-2 !text-[10px] uppercase tracking-wider
                             bg-[var(--warn,#f59e0b)] text-white border-transparent">
              prijsdaling
            </span>
          )}
          <LiveStatusBadge lead={lead} />
        </div>
        <div className="text-[15px] font-semibold text-[var(--ink-900)] truncate">
          {lead.address} · {lead.postal_code} {lead.city}
        </div>
        <div className="text-[12px] text-[var(--ink-500)] mt-1 flex flex-wrap gap-x-3">
          {lead.agent_name && <span>{lead.agent_name}</span>}
          <span>Online {formatTimeSince(lead.date_posted)}</span>
        </div>
      </div>
      <div className="col-span-2 tabular">
        <div className="text-[15px] font-semibold">{formatEuro(lead.price)}</div>
        {hasPriceDrop && (
          <div className="text-[12px] text-[var(--ink-400)] line-through">{formatEuro(lead.old_price)}</div>
        )}
      </div>
      <div className="col-span-3">
        <BriefCountdown countdown={countdown} nextLabel={nextBriefLabel} />
      </div>
      <div className="col-span-2 flex justify-end items-center gap-2">
        {lead.shortlisted && <Bookmark size={16} className="text-[var(--teal-600)] fill-[var(--teal-500)]" />}
        <button type="button" onClick={(e) => { e.stopPropagation(); onCall(); }}
                data-testid={`btn-call-${lead.id}`}
                className="btn btn-ghost h-9 !px-3">
          <Phone size={14} /> {t("leads.call")}
        </button>
        <button type="button" onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
                data-testid={`btn-delete-${lead.id}`}
                title="Lead verwijderen"
                className="p-2 rounded-md text-[#B43232] hover:bg-[#FBE7E7]">
          <Trash2 size={15} />
        </button>
      </div>
    </li>
  );
}

function BriefCountdown({ countdown, nextLabel }) {
  if (!countdown) {
    return <div className="text-[12px] text-[var(--ink-500)]">Geen datum bekend</div>;
  }
  const { daysLeft, dueDate, executedDate } = countdown;
  const isOverdue = daysLeft < 0;
  const isToday = daysLeft === 0;
  const cls = isOverdue
    ? "text-[var(--danger,#B43232)] font-semibold"
    : isToday
      ? "text-[#D97706] font-semibold"
      : daysLeft <= 7
        ? "text-[#D97706]"
        : "text-[var(--ink-700)]";
  return (
    <div className="space-y-1" data-testid="brief-countdown">
      <div className={`text-[13px] tabular flex items-center gap-1.5 ${cls}`}>
        {isOverdue ? <AlertCircle size={13} /> : <Clock size={13} />}
        <span>
          {nextLabel}{" "}
          {isOverdue
            ? <strong data-testid="brief-overdue">{Math.abs(daysLeft)}d te laat</strong>
            : isToday
              ? <strong>vandaag</strong>
              : <strong>over {daysLeft}d</strong>}
        </span>
      </div>
      <div className="text-[11px] text-[var(--ink-500)] tabular">
        Deadline {dueDate} · vorige verzonden {executedDate}
      </div>
    </div>
  );
}

/**
 * Compute days until next brief deadline based on when the current brief
 * was executed + configured `followup_days`.
 */
function computeCountdown(executedAtIso, followupDays) {
  if (!executedAtIso) return null;
  const executed = new Date(executedAtIso);
  if (isNaN(executed.getTime())) return null;
  const deadline = new Date(executed);
  deadline.setDate(deadline.getDate() + (followupDays || 0));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dl = new Date(deadline);
  dl.setHours(0, 0, 0, 0);
  const daysLeft = Math.round((dl - today) / (1000 * 60 * 60 * 24));
  const fmt = (d) => d.toLocaleDateString("nl-BE", { day: "2-digit", month: "short", year: "numeric" });
  return {
    daysLeft,
    dueDate: fmt(deadline),
    executedDate: fmt(executed),
  };
}
